import { useModal } from '@ebay/nice-modal-react'
import { type FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ToastMessage } from '../components/common'
import useGeoCheck from '../hooks/useGeoCheck'
import useStorageReady from '../hooks/useStorageReady'
import { initialUserState } from '../store/slices/userSlice'
// import {
//   addMaintenanceToken,
//   checkMaintenanceToken,
// } from '../utils/maintenance'
import { useBoundStore } from '../store/store'
import type { ApiResponse } from '../types/api'
import type { UserInfo } from '../types/user'
import { api } from '../utils/api-client'
// import closeModal from '../utils/closeModal'
import { getDeviceInfo } from '../utils/device'
import { syncFcmToken } from '../utils/fcmToken'
import { getPublicAuth } from '../utils/getPublicAuth'
import { clearUser, decryptUser } from '../utils/user'

function withLogin(WrappedComponent: FC) {
  const WithLogin = (props: Record<string, unknown>) => {
    const { geoCheck } = useGeoCheck()
    const { i18n } = useTranslation()
    const { language } = i18n
    const setIsUserLoading = useBoundStore(state => state.user.setIsLoading)
    const setUser = useBoundStore(state => state.user.setUser)
    const toastMessage = useModal(ToastMessage)
    // const maintenanceModal = useModal(MaintenanceModal)
    // Gate the silent re-login until the stored user is readable (see
    // useStorageReady): Safari's localStorage can read empty for a moment on
    // the first paint. Resolves immediately once the value is present, instead
    // of the old flat 1s wait that every browser paid.
    const ready = useStorageReady()

    useEffect(() => {
      document.documentElement.lang = language
    }, [])

    useEffect(() => {
      if (!ready) return
      const user = decryptUser()
      if (!user) {
        setUser(initialUserState)
        clearUser()
        setIsUserLoading(false)
        return
      }

      if (user.username === 'test03') {
        document.documentElement.translate = true
      }

      const tryLogin = async () => {
        try {
          const publicAuth = await getPublicAuth()

          const reqBody = {
            ...user,
            submit_type: 'login',
            meta: getDeviceInfo(),
            auth: publicAuth,
          }
          const response = await api.post<ApiResponse<UserInfo>>(
            '/center.php',
            reqBody,
          )
          const { status, info } = response.data
          if (status === 1) {
            const privateAuth = await api.post<
              ApiResponse<string | { auth: string; expired_time: number }>
            >('/link.php', {
              username: info.username,
              password: user.password,
              auth: publicAuth,
            })
            if (
              privateAuth.data.status === 1 &&
              typeof privateAuth.data.info !== 'string'
            ) {
              localStorage.setItem('privateAuth', privateAuth.data.info.auth)
              setUser(info)
              // Register this device's FCM token against the restored member session.
              void syncFcmToken()
            } else {
              setUser(initialUserState)
              clearUser()
            }
          } else {
            setUser(initialUserState)
            clearUser()
          }
        } catch (error) {
          // Transient failure (network, increase.php outage): the server never
          // rejected the credentials, so keep them and the persisted session —
          // the next launch or the 401-refresh path retries. Clearing here
          // would log the user out over a hiccup.
          console.error('Silent re-login failed:', error)
        } finally {
          // Must always settle: sports pages and the register form wait on
          // user.isLoading, and an unhandled rejection used to leave it stuck.
          setIsUserLoading(false)
        }
      }

      // geoCheck(tryLogin, () => {
      //   clearUser()
      // })

      geoCheck(tryLogin)
    }, [ready])

    // useEffect(() => {
    //   // Check if web app is in maintenance
    //   const checkMaintenance = async () => {
    //     const response = await api.post<ApiResponse<string>>(
    //       '/ajax_datav2.php',
    //       {
    //         type: 'game_maintenance',
    //       },
    //     )
    //     const { status } = response.data
    //     const isTokenValid = checkMaintenanceToken()
    //     if (status === 0 && !isTokenValid) {
    //       // Force access by admin
    //       if (window.location.href.includes('admin_test')) {
    //         const password = window.prompt('Please enter admin password')
    //         if (password === 'secret') {
    //           addMaintenanceToken()
    //           closeModal(maintenanceModal)
    //         }
    //       }
    //     }
    //   }

    //   checkMaintenance()
    // }, [])

    useEffect(() => {
      const onOffline = () => {
        toastMessage.show({
          text:
            language === 'en'
              ? 'Please connect to the internet'
              : 'Por favor conectarse a Internet',
        })
      }

      window.addEventListener('offline', onOffline)
      return () => window.removeEventListener('offline', onOffline)
    }, [])

    // Re-register the FCM token when the app returns to the foreground, so a token that rotated
    // while backgrounded re-syncs without waiting for the next launch (mirrors the effect of
    // Flutter's onTokenRefresh). syncFcmToken no-ops outside the native app / when logged out.
    useEffect(() => {
      let lastSync = 0
      const resync = () => {
        if (document.visibilityState !== 'visible') return
        const now = Date.now()
        if (now - lastSync < 5000) return // dedupe visibilitychange + focus bursts
        lastSync = now
        void syncFcmToken()
      }
      document.addEventListener('visibilitychange', resync)
      window.addEventListener('focus', resync)
      return () => {
        document.removeEventListener('visibilitychange', resync)
        window.removeEventListener('focus', resync)
      }
    }, [])

    return <WrappedComponent {...props} />
  }

  return WithLogin
}

export default withLogin
